// src/modules/products/services/product.service.ts

import { API_URL, API_HEADER } from "@modules/shared/enums/common-enum";
import { fetchWithAuth } from "@modules/shared/helpers/fetch-with-auth.helper";
import MediaService from "@modules/media/services/media.service";
import {
  ProductItem,
  ShopDto,
  BrandDto,
  UserInfo,
  CreateProductInput,
  UpdateProductInput,
  mapProductItem,
  mapShopDto,
  mapBrandDto,
} from "../models/product.model";
import {
  fetchAllPaginated,
  buildPagingQuery,
  shouldContinuePaging,
  extractList,
  unwrapEntity,
} from "@modules/shared/services/pagination.service";

type AuthArgs = {
  auth: any;
  setAuth: (auth: any) => void;
  logout: () => void;
};

export class ProductService {
  private readonly mediaService = new MediaService();

  private getHeaders(): HeadersInit {
    return { ...API_HEADER };
  }

  private getFormHeaders(): HeadersInit {
    const headers = { ...API_HEADER } as Record<string, string>;
    delete headers["Content-Type"];
    return headers;
  }

  private async uploadFile(file: File, args: AuthArgs): Promise<string> {
    const formData = new FormData();
    formData.append("file", file);
    const response = await this.mediaService.uploadFile(
      formData,
      args.auth,
      args.setAuth,
      args.logout,
    );
    return (response as any)?.data?.url ?? (response as any)?.url ?? "";
  }

  // ===== Products =====

  async getAllItems(args: AuthArgs): Promise<ProductItem[]> {
    return fetchAllPaginated(async (page: number, limit: number) => {
      const response = await fetchWithAuth(
        `${API_URL.MARKETPLACE_ITEM}?${buildPagingQuery(page, limit, -1)}`,
        { method: "GET", headers: this.getHeaders() },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const items = extractList(response).map(mapProductItem);
      const hasMore = shouldContinuePaging(items, page, limit, response);
      return { items, hasMore };
    });
  }

  async createItem(
    input: CreateProductInput,
    args: AuthArgs,
  ): Promise<ProductItem> {
    const uploadedImages = await Promise.all(
      (input.new_image_files ?? []).map((f) => this.uploadFile(f, args)),
    );
    const images = [
      ...(input.existing_images ?? []).filter(Boolean),
      ...uploadedImages,
    ];
    const response = await fetchWithAuth(
      API_URL.MARKETPLACE_ITEM,
      {
        method: "POST",
        headers: this.getHeaders(),
        body: JSON.stringify({
          name: input.name.trim(),
          shop_id: input.shop_id,
          category: input.category.trim(),
          price_unit: input.price_unit.trim(),
          price: input.price.trim(),
          images,
          options: input.options,
          description: input.description,
          stock: input.stock,
          bought: input.bought ?? 0,
          status: input.status,
          detail_information: input.detail_information,
          discount_percentage: input.discount_percentage,
          payment_method: input.payment_method,
         trade_discount_percentage: input.trade_discount_percentage ?? 0,
        }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapProductItem(unwrapEntity(response));
  }

  async updateItem(
    input: UpdateProductInput,
    args: AuthArgs,
  ): Promise<ProductItem> {
    const uploadedImages = await Promise.all(
      (input.new_image_files ?? []).map((f) => this.uploadFile(f, args)),
    );
    const images = [
      ...(input.existing_images ?? []).filter(Boolean),
      ...uploadedImages,
    ];
    const payload = {
      name: input.name.trim(),
      shop_id: input.shop_id,
      category: input.category.trim(),
      price_unit: input.price_unit.trim(),
      price: input.price.trim(),
      images,
      options: input.options,
      description: input.description,
      stock: input.stock,
      status: input.status,
      detail_information: input.detail_information,
      discount_percentage: input.discount_percentage,
      payment_method: input.payment_method,
      trade_discount_percentage: input.trade_discount_percentage ?? 0
    };

    // PUT với id trong body
    const response = await fetchWithAuth(
      API_URL.MARKETPLACE_ITEM,
      {
        method: "PUT",
        headers: this.getHeaders(),
        body: JSON.stringify({ ...payload, id: input.item_id }),
      },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return mapProductItem(unwrapEntity(response));
  }

  async deleteItem(itemId: string, args: AuthArgs): Promise<boolean> {
    const response = await fetchWithAuth(
      `${API_URL.MARKETPLACE_ITEM}/${encodeURIComponent(itemId)}`,
      { method: "DELETE", headers: this.getHeaders() },
      args.auth,
      args.setAuth,
      args.logout,
    );
    return typeof response === "boolean"
      ? response
      : Boolean(response?.success ?? response);
  }

  // ===== Shops =====

  async getAllShops(args: AuthArgs): Promise<ShopDto[]> {
    return fetchAllPaginated(async (page: number, limit: number) => {
      const response = await fetchWithAuth(
        `${API_URL.MARKETPLACE_SHOP}?${buildPagingQuery(page, limit, -1)}`,
        { method: "GET", headers: this.getHeaders() },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const items = extractList(response).map(mapShopDto);
      const hasMore = shouldContinuePaging(items, page, limit, response);
      return { items, hasMore };
    });
  }

  // ===== Brands =====

  async getAllBrands(args: AuthArgs): Promise<BrandDto[]> {
    return fetchAllPaginated(async (page: number, limit: number) => {
      const response = await fetchWithAuth(
        `${API_URL.B2B}?${buildPagingQuery(page, limit, -1)}`,
        { method: "GET", headers: this.getHeaders() },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const items = extractList(response).map(mapBrandDto);
      const hasMore = shouldContinuePaging(items, page, limit, response);
      return { items, hasMore };
    });
  }

  // ===== Users =====

  async getUserById(userId: string, args: AuthArgs): Promise<UserInfo | null> {
    if (!userId) return null;
    try {
      const response = await fetchWithAuth(
        `${API_URL.USER_BY_ID}/${encodeURIComponent(userId)}`,
        { method: "GET", headers: this.getHeaders() },
        args.auth,
        args.setAuth,
        args.logout,
      );
      const user = response?.data ?? response;
      if (user && typeof user === "object") {
        let avatarUrl = "";
        if (Array.isArray(user.avatars) && user.avatars.length > 0) {
          avatarUrl = user.avatars[0].url || "";
        }
        return {
          id: user.id ?? user._id ?? userId,
          name: user.name ?? user.fullName ?? user.username ?? "",
          email: user.email ?? "",
          avatar: avatarUrl,
        };
      }
      return null;
    } catch {
      return null;
    }
  }
}
